/**
 * Grid Metadata Extractor
 *
 * Extracts metadata from column definitions for StandardDataGrid.
 * Used to derive populate fields, custom components, etc. from configuration.
 */

import React from 'react'
import { RENDER_COUNTRY } from '../constants/renderComponents'
import Country from '../components/Country'

/**
 * Map of render component constants to their actual components
 */
const COMPONENT_REGISTRY = {
  [RENDER_COUNTRY]: Country,
  // Add other custom components here
}

/**
 * Create wrapper component for grid rendering
 * Adapts component props to match grid column utility interface
 */
const createComponentWrapper = (Component, fieldName) => {
  return ({ value, rawValue, row }) => {
    // For Country component, pass stationCode and label
    if (Component === Country) {
      return <Country stationCode={rawValue || row[fieldName]} label={value} />
    }

    // Generic wrapper for other components
    return <Component value={value} rawValue={rawValue} row={row} />
  }
}

/**
 * Extract populate fields from column definitions
 * Returns array of field names that need to be populated/enriched
 *
 * Fields are populated if they:
 * - Have a reference table (need enrichment)
 * - Are user fields (need user details)
 * - Have explicit populate: true flag
 */
export const extractPopulateFields = (columnDefinitions) => {
  if (!columnDefinitions) return []

  const { FIELD_USER, FIELD_EMPLOYEE } = require('../constants/fields')

  return columnDefinitions
    .filter(col => {
      // Explicit populate flag
      if (col.populate === true) return true

      // Reference fields need population for enrichment
      if (col.reference) return true

      // User fields need population for user details
      if (col.field === FIELD_USER) return true

      // Employee fields need population for employee details
      if (col.field === FIELD_EMPLOYEE) return true

      return false
    })
    .map(col => col.value)
}

/**
 * Extract custom components from column definitions
 * Returns object mapping component names to wrapper components
 */
export const extractCustomComponents = (columnDefinitions) => {
  if (!columnDefinitions) return {}

  const components = {}

  columnDefinitions.forEach(col => {
    if (col.renderComponent) {
      const Component = COMPONENT_REGISTRY[col.renderComponent]
      if (Component) {
        // Create a unique key for this component instance
        // eslint-disable-next-line testing-library/render-result-naming-convention
        const registryKey = col.renderComponent
        if (!components[registryKey]) {
          components[registryKey] = createComponentWrapper(Component, col.value)
        }
      }
    }
  })

  return components
}

/**
 * Extract all metadata from column definitions
 */
export const extractGridMetadata = (columnDefinitions) => {
  return {
    populateFields: extractPopulateFields(columnDefinitions),
    customComponents: extractCustomComponents(columnDefinitions)
  }
}
